import { Clock, ClockCheck, Timer } from "lucide-react";

import type { CampaignForImage } from "./campaign-for-image";
import { Typography } from "../ui/typography";
import { cn } from "@/lib/utils";
import { campaignDisplayTitle } from "@/utils/campaign-helpers";
import { parseEndDate, useTimeRemaining } from "./campaign-time-remaining";
import { useTranslation } from "react-i18next";

const STATUS_FINISHED = "FI";

export const CampaignDurationBadge = ({
  campaign,
}: {
  campaign: CampaignForImage;
}) => {
  const { t } = useTranslation();
  const endDate = parseEndDate(campaign.end);
  const remaining = useTimeRemaining(
    endDate,
    t,
    true,
    campaign.status !== STATUS_FINISHED
  );
  return (
    <div className="absolute right-0 bottom-0 flex w-auto items-center gap-1 rounded-tl-md bg-background/70 px-1 py-0.5">
      <Timer className="size-4 text-muted-foreground" />
      <Typography tag="h6">{remaining}</Typography>
    </div>
  );
};

export const CampaignDuration = ({
  campaign,
  onlyTitle = false,
}: {
  campaign: CampaignForImage;
  onlyTitle?: boolean;
}) => {
  const { t } = useTranslation();
  const endDate = parseEndDate(campaign.end);
  const isFinished =
    campaign.status === STATUS_FINISHED || endDate.getTime() < Date.now();
  const label = campaignDisplayTitle(campaign, t);
  // No ticker is rendered when finished or title-only — don't run a timer.
  const remaining = useTimeRemaining(endDate, t, !onlyTitle && !isFinished);

  if (onlyTitle) {
    return (
      <Typography tag="h5" className="text-inherit">
        {label}
      </Typography>
    );
  }

  return (
    <div
      className={cn(
        "flex w-auto items-center gap-1 rounded-tl-md bg-secondary px-1 py-0.5 text-primary",
        isFinished && "text-urgent"
      )}
    >
      {isFinished ? (
        <>
          <ClockCheck className="size-4 text-inherit" />
          <Typography tag="h5" className="text-inherit">
            {t("campaign.isFinished", { label })}
          </Typography>
        </>
      ) : (
        <>
          <Clock className="size-4 text-inherit" />
          <Typography tag="h5" className="text-inherit">
            {t("campaign.endsIn", { label })} {remaining}
          </Typography>
        </>
      )}
    </div>
  );
};
